import type { CatItem } from './batch';
import { state } from './state';
import { requireEl } from './dom';
import { fetchLikeCounts, getLikeInfo, onLikeChange, toggleLike } from './likes';
import { copyShareLink, openImageUrl } from './share';
import { Config } from './config';
import { syncComments } from './comments';

const SRC_LABELS: Record<string, string> = {
  cataas: 'cataas',
  thecatapi: 'thecatapi',
  user: 'своё',
  gif: 'gif',
  breeds: 'breeds',
  jpg: 'jpg',
};

function formatMeta(c: CatItem, index: number): string {
  const w = c.width ?? Config.IMG_W;
  const h = c.height ?? Config.IMG_H;
  const src = SRC_LABELS[c.src] ?? c.src;
  return `#${index + 1} · ${src} · ${w}×${h}`;
}

export function createLightbox() {
  const lb = requireEl<HTMLElement>('lb');
  const zoom = requireEl<HTMLImageElement>('zoom');
  const info = requireEl<HTMLElement>('lb-info');
  const likeBtn = requireEl<HTMLButtonElement>('lb-like');
  const copyBtn = requireEl<HTMLButtonElement>('lb-copy');
  const openBtn = requireEl<HTMLButtonElement>('lb-open');

  function renderLike(url: string): void {
    const data = getLikeInfo(url);
    likeBtn.classList.toggle('on', Boolean(data?.liked));
    const count = data?.count ?? 0;
    if (data?.liked) {
      likeBtn.textContent = count > 1 ? `♥ ${count}` : '♥';
    } else if (count > 0) {
      likeBtn.textContent = `♥ ${count}`;
      likeBtn.title = `лайков: ${count}`;
    } else {
      likeBtn.textContent = '♥';
      likeBtn.title = 'лайкнуть';
    }
    likeBtn.setAttribute('aria-pressed', data?.liked ? 'true' : 'false');
  }

  function show(): void {
    const c = state.allCats[state.idx];
    if (!c) return;
    zoom.src = c.url;
    info.textContent = formatMeta(c, state.idx);
    renderLike(c.url);
    void fetchLikeCounts([c.url]).then(() => renderLike(c.url));
    syncComments(c.publicId);
  }

  function open(i: number): void {
    state.idx = i;
    show();
    lb.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function close(): void {
    lb.classList.remove('open');
    zoom.src = '';
    document.body.style.overflow = '';
    syncComments(null);
  }

  function step(d: number): void {
    if (!state.allCats.length) return;
    state.idx = (state.idx + d + state.allCats.length) % state.allCats.length;
    show();
  }

  likeBtn.onclick = e => {
    e.stopPropagation();
    const c = state.allCats[state.idx];
    if (!c) return;
    likeBtn.disabled = true;
    void toggleLike(c.url).finally(() => {
      likeBtn.disabled = false;
      renderLike(c.url);
    });
  };

  copyBtn.onclick = e => {
    e.stopPropagation();
    const c = state.allCats[state.idx];
    if (!c) return;
    copyBtn.disabled = true;
    void copyShareLink(c).then(ok => {
      copyBtn.disabled = false;
      if (ok) {
        copyBtn.classList.add('flash');
        window.setTimeout(() => copyBtn.classList.remove('flash'), 1200);
      }
    });
  };

  openBtn.onclick = e => {
    e.stopPropagation();
    const c = state.allCats[state.idx];
    if (c) openImageUrl(c.url);
  };

  onLikeChange((url, info) => {
    const c = state.allCats[state.idx];
    if (c?.url === url) {
      likeBtn.classList.toggle('on', info.liked);
      likeBtn.textContent = info.count > 0 ? `♥ ${info.count}` : '♥';
      likeBtn.setAttribute('aria-pressed', info.liked ? 'true' : 'false');
    }
  });

  requireEl<HTMLButtonElement>('close').onclick = close;
  requireEl<HTMLButtonElement>('prev').onclick = e => { e.stopPropagation(); step(-1); };
  requireEl<HTMLButtonElement>('next').onclick = e => { e.stopPropagation(); step(1); };
  lb.onclick = e => { if ((e.target as HTMLElement).id === 'lb') close(); };
  document.addEventListener('keydown', e => {
    if (!lb.classList.contains('open')) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') step(-1);
    if (e.key === 'ArrowRight') step(1);
  });

  let touchStartX = 0;
  let touchStartY = 0;
  lb.addEventListener('touchstart', e => {
    if (e.touches.length !== 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });
  lb.addEventListener('touchend', e => {
    if (!lb.classList.contains('open')) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchStartX;
    const dy = t.clientY - touchStartY;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
    step(dx < 0 ? 1 : -1);
  }, { passive: true });

  return { open, close, show };
}
